import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/api/axios.js'
import { min, max, filter, map, orderBy, sortBy, zipWith, groupBy, keyBy, sumBy, uniqBy } from 'lodash'
import * as d3 from 'd3'
import { sum } from 'd3'
import i18n from '@/i18n'

Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        locale: i18n.locale,
        chancellorProb: null,
        eventProb: null,
        colorsPartyRGB: {
            "CDU/CSU": "rgb(50, 48, 46)", "SPD": "rgb(227, 0, 15)", "Grüne": "rgb(100, 161, 45)", "Linke": "rgb(104, 24, 148)",
            "FDP": "rgb(255, 237, 0)", "AfD": "rgb(0, 158, 224)", "other": "rgb(130, 130, 130)"
        },
        coalitionParties: {
            1: ["CDU/CSU", "SPD"],
            2: ["SPD", "Linke", "Grüne"], 
            3: ["SPD", "FDP", "Grüne"],
            4: ["CDU/CSU", "Grüne"], 
            5: ["SPD", "Grüne"], 
            6: ["SPD", "CDU/CSU"], 
            7: ["CDU/CSU", "FDP"],
            8: ["CDU/CSU", "FDP", "Grüne"], // Jamaika
            9: ["Grüne", "SPD"], 
            10: ["Grüne", "SPD", "Linke"],
            11: ["Grüne", "SPD", "FDP"], 
            12: ["Grüne", "CDU/CSU"], 
            13: ["Grüne", "CDU/CSU", "FDP"], // Jamaika 2
            14: ["CDU/CSU", "SPD", "FDP"], // Schland 1
            15: ["SPD", "CDU/CSU", "FDP"], // Schland 2
            16: ["CDU/CSU", "Grüne", "SPD"], // Kenia
            17: ["Grüne", "CDU/CSU", "SPD"], // Kenia 2
            18: ["SPD", "CDU/CSU", "Grüne"], // Kenia 3
        },
        partOfGovernment: null,
        coalitionProb: null,
        appLanguage: "german",
        forecast: null,
        forecastAll: null,
        survey: null,
        partyId: {
            "CDU/CSU": 1,
            "SPD": 2,
            "AfD": 3,
            "Grüne": 4,
            "Linke": 5,
            "FDP": 6
        },
        lastUpdate: null,
        windowWidth: 1000
    },
    getters: {
        topFive(state) {
            if (!state.coalitionProb) return null;

            let grouped = groupBy(state.coalitionProb, x => x.coalition_id);
            let last = map(grouped, o => o[o.length - 1]);
            last = orderBy(last, ["estimate"], ['desc']);

            return filter(last, o => o.estimate >= 0.01);
        },
        topFiveCoalitionsChartData(state, getters) {
            if (!getters.topFive) return { points: [] };

            return {
                points: map(getters.topFive, o => ({
                    id: o.coalition_id,
                    title: getters.coalitionNameMapping[o.coalition_id],
                    x: Math.round(o.estimate * 100)/100,
                    estimate: o.estimate,
                    parties: map(state.coalitionParties[o.coalition_id], p => {
                        return state.partyId[p]
                    }),
                    colors: map(state.coalitionParties[o.coalition_id], p => {
                        return state.colorsPartyRGB[p];
                    })
                }))
            }
        },
        chancellorChartData(state) {
            if (!state.coalitionProb) return { points: [] };

            let grouped = groupBy(state.coalitionProb, x => x.coalition_id);
            let last = map(grouped, o => o[o.length - 1]);

            let chancellors = groupBy(last, 'chancellor');

            let data = map(chancellors, o => ({
                id: 'chancellor-' + o[0].coalition_id,
                title: o[0].chancellor,
                x:  Math.floor(sumBy(o, 'estimate') * 100)/100,
                estimate: sumBy(o, 'estimate'),
                parties: map(state.coalitionParties[o[0].coalition_id].slice(0, 1), p => {
                    return state.partyId[p]
                }),
                colors: map(state.coalitionParties[o[0].coalition_id].slice(0, 1), p => {
                    return state.colorsPartyRGB[p];
                })
            }));

            while(sum(map(data, "x")) < 1) {
                let diff = map(data, o => o.estimate - o.x);
                let maxdiff = max(diff);

                let index = diff.indexOf(maxdiff);

                data[index].x = data[index].x + 0.01;
            }

            return {
                points: orderBy(data, ["x"], ["desc"])
            }
        },
        chancellorLineChartData(state) {
            if (!state.coalitionProb) return { lines: [] };

            let grouped = groupBy(state.coalitionProb, x => x.chancellor);

            let chartData = {
                lines: map(grouped, o => {
                    o = filter(o, x => x.date_forecast > "2021-04-19")
                    
                    let dates = groupBy(o, "date_forecast")

                    let points = map(dates, x => (
                        {
                            x: x[0].date_forecast,
                            y: Math.floor(sumBy(x, "estimate") * 100)/100,
                            estimate: sumBy(x, "estimate")
                        }
                    ))

                    return {
                        id: 'chancellor-' + o[0].coalition_id,
                        title: o[0].chancellor,
                        color: "party",
                        points: points,
                        parties: map(state.coalitionParties[o[0].coalition_id].slice(0, 1), p => {
                            return state.partyId[p]
                        }),
                        colors: map(state.coalitionParties[o[0].coalition_id].slice(0, 1), p => {
                            return state.colorsPartyRGB[p];
                        })
                    }
                })
            }

            /* make sure all points over different chancellors add up to 100% */
            for (let i = 0; i < chartData.lines[0].points.length; i ++) {
                let data = map(chartData.lines, line => line.points[i])

                while(sum(map(data, "y")) < 1) {
                    let diff = map(data, o => o.estimate - o.y);
                    let maxdiff = max(diff);
    
                    let index = diff.indexOf(maxdiff);
    
                    data[index].y = data[index].y + 0.01;
                }
                
                for (let j = 0; j < data.length; j ++) {
                    chartData.lines[j].points[i] = data[j];
                }
            }

            return chartData;
        },
        coalitionProb(state) {
            if (!state.coalitionProb) return null;

            let grouped = groupBy(state.coalitionProb, x => x.coalition_id);
            let last = map(grouped, o => o[o.length - 1]);
            last = orderBy(last, ["estimate"], ['desc']);
            return last;
        },
        topFiveCoalitionsLineChartData(state, getters) {
            if (!getters.topFive) return { lines: [] };

            let topFiveIds = map(getters.topFive, "id");

            let topFiveProbs = filter(state.coalitionProb, o => topFiveIds.indexOf(o.id) != -1);

            let grouped = groupBy(topFiveProbs, x => x.coalition_id);

            return {
                lines: map(grouped, o => {
                    o = filter(o, x => x.date_forecast > "2020-12-11")
                    o = orderBy(o, ["date_forecast"]);

                    let forecast = map(o, "date_forecast");
                    let estimate = map(o, "estimate");

                    return {
                        id: o[0].coalition_id,
                        title: getters.coalitionNameMapping[o[0].coalition_id],
                        color: 'neutral',
                        points: zipWith(forecast, estimate, (date, estimate) => ({
                            x: date,
                            y: Math.round(estimate * 100)/100,
                            estimate: estimate
                        })),
                        parties: map(state.coalitionParties[o[0].coalition_id], p => {
                            return state.partyId[p]
                        }),
                        colors: map(state.coalitionParties[o[0].coalition_id], p => {
                            return state.colorsPartyRGB[p];
                        })
                    }
                })
            }
        },
        partOfGovernmentChartData(state, getters) {
            if (!state.partOfGovernment) return { points: [] };

            let grouped = groupBy(state.partOfGovernment, x => x.party_id);
            let last = map(grouped, o => o[o.length - 1]);

            last = filter(last, o => o.descr_german != "AfD");

            return {
                points: map(orderBy(last, ["estimate"], ["desc"]), o => ({
                    x: Math.round(o.estimate * 100)/100,
                    estimate: o.estimate,
                    id: o.id,
                    color: state.colorsPartyRGB[o.descr_german],
                    title: getters.partyInChart[o.id]
                }))
            }
        },
        forecastChartData(state, getters) {
            if (!state.forecastAll) return { lines: [], lastUpdate: new Date().toISOString() };

            let forecast = filter(state.forecastAll, o => (o.date_forecast >= '2018-12-28' && o.date_forecast <= '2021-09-26'))

            let grouped = groupBy(forecast, "party_id");

            return {
                lastUpdate: getters.lastUpdate,
                lines: map(grouped, o => {
                    let forecast = map(o, "date_forecast");
                    let estimate = map(o, "estimate");
                    let upper = map(o, "upper_bound")
                    let lower = map(o, "lower_bound")

                    return {
                        id: o[0].party_id,
                        title: getters.partyInChart[o[0].party_id],
                        points: zipWith(forecast, estimate, upper, lower, (date, estimate, upper, lower) => ({
                            x: date,
                            y: estimate,
                            upper: upper,
                            lower: lower
                        })),
                        color: state.colorsPartyRGB[o[0].descr_german]
                    }
                })
            }
        },
        forecastTableData(state, getters) {
            if (!state.forecast) return [];

            let lastForecastDate = max(map(state.forecast, "date_forecast"));

            let grouped = map(groupBy(state.forecast, "party_id"), o => {

                let forecasts = filter(o, { "date_forecast": lastForecastDate });

                let f = keyBy(forecasts, "type");

                let electionDay = filter(o, { "type": "election_day" });
                
                let dates = map(electionDay, x => Math.abs((new Date(lastForecastDate).getTime() - new Date(x["date_forecast"]).getTime()) / (24 * 60 * 60 * 1000) - 14));
                let twoweeksago = dates.indexOf(min(dates));

                let lastForecast = electionDay[twoweeksago].estimate;

                let trend = {value: f["election_day"].estimate - lastForecast}

                if (trend.value >= 0.003) {
                    trend.direction = "up";
                } else if (trend.value <= -0.003) {
                    trend.direction = "down";
                } else {
                    trend.direction = "constant"
                }

                return {
                    party: getters.partyInChart[f["next_sunday"].party_id],
                    party_id: f["next_sunday"].party_id,
                    nowcast: f["next_sunday"].estimate,
                    forecast: f["election_day"].estimate,
                    forecast_upper: f["election_day"].upper_bound,
                    forecast_lower: f["election_day"].lower_bound,
                    trend: trend,
                    color: state.colorsPartyRGB[f["next_sunday"].descr_german]
                }
            });

            return orderBy(grouped, ["forecast"], ["desc"]);
        },
        survey (state) {
            if (!state.survey) return null;

            let survey = filter(state.survey, o => o.institute != '')

            let parser = d3.timeParse("%Y-%m-%d");

            return map(survey, o => ({
                date: parser(o.date).toISOString(),
                institute: o.institute,
                estimate: o.estimate,
                party_id: o.party_id
            }));
        },
        lastPolls (getters) {
            if (!getters.survey) return null;

            let grouped = groupBy(getters.survey, o => (o.date + o.institute));

            grouped = map(grouped, o => keyBy(o, "party_id"));

            return uniqBy(grouped, o => o["1"].institute);
        },
        eventTableData(state, getters) {
            if (!state.eventProb) return [];

            let grouped = groupBy(state.eventProb, "event_id");

            let events = map(grouped, o => {
                let event = o[o.length - 1];

                event.predictions = map(o, x => ({ date: x.date_forecast, estimate: x.estimate })).slice(-30);

                if (!(event.id in getters.eventNameMapping)) return null;

                event.title = getters.eventNameMapping[event.id];

                return event;
            })

            events = filter(events);

            return orderBy(events, ["estimate"], ["desc"])
        },
        lastUpdate (state) {
            if (!state.lastUpdate) return null;

            let parser = d3.timeParse("%Y-%m-%d %H:%M:%S");

            return parser(state.lastUpdate).toISOString();
        },
        eventNameMapping (state) {
            /* eslint-disable */
            /* force reload on locale change */
            let locale = state.locale;
            /* eslint-enable */

            return {
                1: i18n.t("events_1"),
                2: i18n.t("events_2"),
                3: i18n.t("events_3"),
                4: i18n.t("events_4"),
                5: i18n.t("events_5"),
                6: i18n.t("events_6"),
                7: i18n.t("events_7"),
                8: i18n.t("events_8"),
                9: i18n.t("events_9"),
                10: i18n.t("events_10"),
//                11: i18n.t("events_11"),
                12: i18n.t("events_12"),
                13: i18n.t("events_13"),
                14: i18n.t("events_14"),
                15: i18n.t("events_15"),
                17: i18n.t("events_17"),
                18: i18n.t("events_18"),
                19: i18n.t("events_19"),
                20: i18n.t("events_20"),
                21: i18n.t("events_21"),
                22: i18n.t("events_22"),
                23: i18n.t("events_23")
            }
        },
        coalitionNameMapping (state) {
            /* eslint-disable */
            /* force reload on locale change */
            let locale = state.locale;
            /* eslint-enable */

            return {
                1: i18n.t("coalition_name_1"),
                2: i18n.t("coalition_name_2"),
                3: i18n.t("coalition_name_3"),
                4: i18n.t("coalition_name_4"),
                5: i18n.t("coalition_name_5"),
                6: i18n.t("coalition_name_6"),
                7: i18n.t("coalition_name_7"),
                8: i18n.t("coalition_name_8"),
                9: i18n.t("coalition_name_9"),
                10: i18n.t("coalition_name_10"),
                11: i18n.t("coalition_name_11"),
                12: i18n.t("coalition_name_12"),
                13: i18n.t("coalition_name_13"),
                14: i18n.t("coalition_name_14"),
                15: i18n.t("coalition_name_15"),
                16: i18n.t("coalition_name_16"),
                17: i18n.t("coalition_name_17"),
                18: i18n.t("coalition_name_18"),
            }
        },
        partyInChart (state) {
            /* eslint-disable */
            /* force reload on locale change */
            let locale = state.locale;
            /* eslint-enable */

            return {  
                1: i18n.t('other_party_chart_1'), 
                2: i18n.t('other_party_chart_2'), 
                3: i18n.t('other_party_chart_3'), 
                4: i18n.t('other_party_chart_4'), 
                5: i18n.t('other_party_chart_5'), 
                6: i18n.t('other_party_chart_6')
            }
        },
        partyInText (state) {
            /* eslint-disable */
            /* force reload on locale change */
            let locale = state.locale;
            /* eslint-enable */

            return {  
                1: i18n.t('other_party_text_1'),
                2: i18n.t('other_party_text_2'),
                3: i18n.t('other_party_text_3'),
                4: i18n.t('other_party_text_4'),
                5: i18n.t('other_party_text_5'),
                6: i18n.t('other_party_text_6')
            }
        },
    },
    mutations: {
        updateLocale(state, newLocale) {
            state.locale = newLocale
        },
        setChancellorProb(state, data) {
            state.chancellorProb = data;
        },
        setEventProb(state, data) {
            state.eventProb = sortBy(data, ["date_forecast"]);
        },
        setPartOfGovernment(state, data) {
            state.partOfGovernment = sortBy(data, ["date_forecast"]);
        },
        setCoalitionProb(state, data) {
            state.coalitionProb = sortBy(data, ["date_forecast"]);
        },
        setForecast(state, data) {
            state.forecast = sortBy(data, ["date_forecast"]);
        },
        setForecastAll(state, data) {
            state.forecastAll = sortBy(data, ["date_forecast"]);
        },
        setLastUpdate(state, data) {
            state.lastUpdate = data.time;
        },
        setSurvey(state, data) {
            state.survey = orderBy(data, ["date"], ["desc"]);
        },
        updateDimensions(state) {
            state.windowWidth = window.innerWidth;
        }
    },
    actions: {
        getChancellorProb({ commit }) {
            api.getChancellorProb()
                .then(response => commit('setChancellorProb', response.data));
        },
        getEventProb({ commit }) {
            api.getEventProb()
                .then(response => commit('setEventProb', response.data));
        },
        getPartOfGovernment({ commit }) {
            api.getPartOfGovernment()
                .then(response => commit('setPartOfGovernment', response.data));
        },
        getCoalitionProb({ commit }) {
            api.getCoalitionProb()
                .then(response => commit('setCoalitionProb', response.data));
        },
        getForecast({ commit }) {
            api.getForecast()
                .then(response => commit('setForecast', response.data));
        },
        getForecastAll({ commit }) {

            api.getForecastAll()
                .then(response => commit('setForecastAll', response.data));
        },
        getLastUpdate({commit}) {
            api.getLastUpdate()
               .then(response => commit('setLastUpdate', response.data));
        },
        getSurvey({commit}) {
            api.getSurvey()
               .then(response => commit('setSurvey', response.data));
        },
        changeLocale({ commit }, newLocale) {
            i18n.locale = newLocale // important!

            if (i18n.locale == 'de') {
                d3.timeFormatDefaultLocale({
                    "decimal": ",",
                    "thousands": ".",
                    "grouping": [3],
                    "currency": ["€", ""],
                    "dateTime": "%a %b %e %X %Y",
                    "date": "%d.%m.%Y",
                    "time": "%H:%M:%S",
                    "periods": ["AM", "PM"],
                    "days": ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
                    "shortDays": ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
                    "months": ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
                    "shortMonths": ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]
                });
            } else {
                d3.timeFormatDefaultLocale({
                    "decimal": ".",
                    "thousands": ",",
                    "grouping": [3],
                    "currency": ["€", ""],
                    "dateTime": "%a %b %e %X %Y",
                    "date": "%d.%m.%Y",
                    "time": "%H:%M:%S",
                    "periods": ["AM", "PM"],
                    "days": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                    "shortDays": ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
                    "months": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
                    "shortMonths": ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
                });
            }
              

            commit('updateLocale', newLocale)
        }
    },
    modules: {
    }
})
